import React, { useCallback } from 'react';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Form, Formik } from 'formik';
import { TranslateFunction } from 'react-localize-redux';

import { formikValidate } from '../../../helpers/validation';

const StyledTextField = styled(TextField)({
  width: '100px',
  marginTop: '8px',
});

const StyledBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

function validateRequiredAndMin(value: number | string, min: number) {
  return formikValidate.required(value.toString()) || formikValidate.min(min)(value) || '';
}

interface RadiusFormProps {
  initialValues: {
    kilometers: number;
    miles: number;
  };
  onRadiusChange: (values: { kilometers: number; miles: number }) => void;
  disabled?: boolean;
  translate: TranslateFunction;
}

export const RadiusForm: React.FC<RadiusFormProps> = ({
  initialValues,
  onRadiusChange,
  disabled,
  translate,
}) => {
  const handleKilometersChange = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement>,
      handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    ) => {
      handleChange(e);
      const km = Number(e.target.value);
      if (!isNaN(km) && km > 0) {
        const miles = (km * 0.621371).toFixed(2);
        onRadiusChange({ kilometers: km, miles: Number(miles) });
      }
    },
    [onRadiusChange]
  );

  const handleMilesChange = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement>,
      handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    ) => {
      handleChange(e);
      const miles = Number(e.target.value);
      if (!isNaN(miles) && miles > 0) {
        const km = (miles / 0.621371).toFixed(2);
        onRadiusChange({ kilometers: Number(km), miles });
      }
    },
    [onRadiusChange]
  );

  return (
    <Formik
      initialValues={initialValues}
      validate={(values) => {
        const errors: Record<string, string> = {};
        errors.kilometers = validateRequiredAndMin(values.kilometers, 0.1);
        errors.miles = validateRequiredAndMin(values.miles, 0.1);
        return errors;
      }}
      onSubmit={onRadiusChange}
      enableReinitialize
    >
      {({ values, handleChange, handleBlur, errors, touched }) => (
        <Form>
          <StyledBox>
            <Typography variant="h5">{translate('Radius')}</Typography>
            <Divider />
            <StyledTextField
              size="small"
              type="number"
              name="kilometers"
              value={values.kilometers}
              onChange={(e) =>
                handleKilometersChange(e as React.ChangeEvent<HTMLInputElement>, handleChange)
              }
              onBlur={handleBlur}
              disabled={disabled}
              error={touched.kilometers && Boolean(errors.kilometers)}
              helperText={touched.kilometers && errors.kilometers}
              inputProps={{
                step: 0.1,
                min: 0,
              }}
              label={translate('Kilometers')}
            />
            <StyledTextField
              size="small"
              type="number"
              name="miles"
              value={values.miles}
              onChange={(e) =>
                handleMilesChange(e as React.ChangeEvent<HTMLInputElement>, handleChange)
              }
              onBlur={handleBlur}
              disabled={disabled}
              error={touched.miles && Boolean(errors.miles)}
              helperText={touched.miles && errors.miles}
              inputProps={{
                step: 0.1,
                min: 0,
              }}
              label={translate('Miles')}
            />
          </StyledBox>
        </Form>
      )}
    </Formik>
  );
};

export default RadiusForm;
